import React, { useState, useMemo, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import { useProducts, Product } from '../../hooks/useProducts';
import { useCart } from '../../context/CartContext';
import { useSEO } from '../../hooks/useSEO';

const ProductDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { products, loading, error } = useProducts();
  const { addItem: addToCart } = useCart();
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [addedToEnquiry, setAddedToEnquiry] = useState(false);

  const product = useMemo(() => {
    if (!id || !products.length) return null;
    return products.find((p: Product) => String(p.id) === id) ?? null;
  }, [id, products]);

  // Dynamic SEO
  useSEO({
    title: product?.name,
    description: product ? `Request a quote for ${product.name}. High-quality ${product.tag || 'medical'} equipment for hospital and laboratory use in Zimbabwe and Africa.` : undefined,
    canonical: `/shop/${id}`,
    ogImage: product?.img,
    schema: product ? {
      "@context": "https://schema.org/",
      "@graph": [
        {
          "@type": "Product",
          "name": product.name,
          "image": [product.img],
          "description": `Professional-grade ${product.tag || 'medical'} equipment for hospital and laboratory use.`,
          "brand": {
            "@type": "Brand",
            "name": "Manlab Medicals"
          },
          "offers": {
            "@type": "Offer",
            "url": `https://www.manlab.co.zw/shop/${id}`,
            "availability": "https://schema.org/InStock",
            "priceCurrency": "USD"
          }
        },
        {
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://www.manlab.co.zw/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Shop",
              "item": "https://www.manlab.co.zw/shop"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": product.name,
              "item": `https://www.manlab.co.zw/shop/${id}`
            }
          ]
        }
      ]
    } : undefined
  });

  // Thumbnails: use same image 3 times as "views" (we only have one img per product)
  const thumbnails = product?.img
    ? [product.img, product.img, product.img]
    : [];

  const recentlyViewed = useMemo(() => {
    if (!product || !products.length) return [];
    return products
      .filter((p: Product) => String(p.id) !== String(product.id))
      .slice(0, 6);
  }, [product, products]);

  const recentScrollRef = useRef<HTMLDivElement>(null);
  const scrollRecently = (dir: number) => {
    if (recentScrollRef.current) {
      recentScrollRef.current.scrollBy({ left: dir * 220, behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <section className="min-h-screen bg-[#FAFAFA] flex items-center justify-center pt-24">
        <div className="animate-pulse text-primary font-semibold">Loading product...</div>
      </section>
    );
  }

  if (error || !product) {
    return (
      <section className="min-h-screen bg-[#FAFAFA] flex flex-col items-center justify-center gap-4 px-4 pt-24">
        <p className="text-gray-600 font-semibold">Product not found.</p>
        <Link to="/shop" className="btn-primary">Back to Shop</Link>
      </section>
    );
  }

  const mainImage = thumbnails[selectedImageIndex] || product.img;

  return (
    <section className="min-h-screen bg-white pt-24">
      <div className="container-main py-8 md:py-12">
        {/* Two-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Left: Gallery */}
          <div className="flex flex-col md:flex-row gap-4">
            {/* Thumbnails (vertical on desktop) */}
            {thumbnails.length > 0 && (
              <div className="flex md:flex-col gap-2 order-2 md:order-1">
                {thumbnails.map((src, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setSelectedImageIndex(i)}
                    className={`w-16 h-16 md:w-20 md:h-20 rounded-lg overflow-hidden border-2 flex-shrink-0 bg-[#F7F9FC] flex items-center justify-center ${
                      selectedImageIndex === i ? 'border-primary' : 'border-transparent'
                    }`}
                  >
                    {src ? (
                      <img src={src} alt={`${product.name} thumbnail ${i + 1}`} className="w-full h-full object-contain" referrerPolicy="no-referrer" />
                    ) : (
                      <MedicalServicesIcon className="text-gray-300" fontSize="large" />
                    )}
                  </button>
                ))}
              </div>
            )}
            {/* Main image */}
            <div className="flex-1 min-h-[300px] md:min-h-[480px] bg-[#F7F9FC] rounded-2xl flex items-center justify-center p-6 border border-gray-100 order-1 md:order-2">
              {mainImage ? (
                <img
                  src={mainImage}
                  alt={product.name}
                  className="max-w-full max-h-[420px] w-auto object-contain"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <MedicalServicesIcon sx={{ fontSize: 120 }} className="text-gray-300" />
              )}
            </div>
          </div>

          {/* Right: Info & actions */}
          <div className="flex flex-col">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2 flex items-center gap-2">
              {product.name}
              <button type="button" className="p-1.5 rounded-full hover:bg-gray-100 text-gray-400 hover:text-primary transition-colors" aria-label="Add to wishlist">
                <FavoriteBorderIcon />
              </button>
            </h1>
            <p className="text-gray-600 text-sm md:text-base leading-relaxed mb-4">
              {product.tag
                ? `Professional-grade ${product.tag} for hospital and laboratory use. Engineered for durability, precision, and reliable performance.`
                : 'High-quality medical equipment for professional healthcare environments. CE certified and built for demanding clinical use.'}
            </p>

            <div className="flex items-baseline gap-2 mb-4">
              <span className="text-2xl md:text-3xl font-bold text-gray-900">Request Quote</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
              <span className="flex text-accent" aria-hidden>
                ★ ★ ★ ★ ★
              </span>
              <span>Request pricing for bulk or single order</span>
            </div>

            {/* Category (instead of colour) */}
            <div className="mb-6">
              <p className="text-sm font-semibold text-gray-700 mb-2">Category</p>
              <div className="flex flex-wrap gap-2">
                <span className="px-4 py-2 bg-gray-100 text-gray-800 rounded-full text-sm font-medium capitalize">
                  {product.tag || product.cat || 'Medical Supplies'}
                </span>
              </div>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <button
                type="button"
                onClick={() => navigate('/contact')}
                className="btn-outline flex-1 sm:flex-none justify-center py-3.5 px-6"
              >
                Request Quote
              </button>
              <button
                type="button"
                onClick={() => {
                  addToCart(product);
                  setAddedToEnquiry(true);
                  setTimeout(() => setAddedToEnquiry(false), 2000);
                }}
                className="btn-primary flex-1 sm:flex-none justify-center py-3.5 px-6"
              >
                {addedToEnquiry ? 'Added to enquiry ✓' : 'Add to enquiry'}
              </button>
            </div>

            {/* Delivery / lead time */}
            <div className="border-t border-gray-100 pt-6 space-y-3">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <InfoOutlinedIcon fontSize="small" className="text-gray-400" />
                <span>Typically dispatched in 5–7 working days</span>
                <ChevronRightIcon fontSize="small" className="text-gray-400" />
              </div>
              <button type="button" className="text-sm text-primary font-semibold hover:underline flex items-center gap-1">
                Why the longer lead time?
                <ChevronRightIcon fontSize="small" />
              </button>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <ChevronRightIcon fontSize="small" className="text-gray-400" />
                <span>Delivery options and costs at checkout</span>
              </div>
            </div>
          </div>
        </div>

        {/* Recently viewed */}
        {recentlyViewed.length > 0 && (
          <div className="mt-16 pt-12 border-t border-gray-100">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Recently viewed</h2>
              <div className="flex gap-1">
                <button
                  type="button"
                  onClick={() => scrollRecently(-1)}
                  className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-50 transition-colors"
                  aria-label="Previous"
                >
                  <ArrowBackIosNewIcon sx={{ fontSize: 14 }} />
                </button>
                <button
                  type="button"
                  onClick={() => scrollRecently(1)}
                  className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-50 transition-colors"
                  aria-label="Next"
                >
                  <ArrowForwardIosIcon sx={{ fontSize: 14 }} />
                </button>
              </div>
            </div>
            <div
              ref={recentScrollRef}
              className="flex gap-4 overflow-x-auto hide-scrollbar pb-2"
              style={{ scrollSnapType: 'x mandatory' }}
            >
              {recentlyViewed.map((p: Product) => (
                <Link
                  key={p.id}
                  to={`/shop/${p.id}`}
                  className="flex-shrink-0 w-[180px] md:w-[200px] group rounded-xl overflow-hidden border border-gray-100 hover:shadow-lg transition-shadow"
                  style={{ scrollSnapAlign: 'start' }}
                >
                  <div className="aspect-square bg-[#F7F9FC] flex items-center justify-center p-4">
                    {p.img ? (
                      <img
                        src={p.img}
                        alt={p.name}
                        className="w-full h-full object-contain group-hover:scale-105 transition-transform"
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      <MedicalServicesIcon className="text-gray-300" sx={{ fontSize: 48 }} />
                    )}
                  </div>
                  <div className="p-3 bg-white">
                    <p className="font-semibold text-gray-900 text-sm truncate">{p.name}</p>
                    <p className="text-primary text-sm font-bold mt-0.5">Request Quote</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ProductDetail;
